import React from 'react'

import { useTranslation } from 'react-i18next'

// MUI Imports
import {
  Typography,
  Box,
  Button,
  IconButton,
  InputBase,
  Tooltip,
  Chip
} from '@mui/material'
import {
  MailOutline,
  Add,
  Search,
  Cached,
  AttachFile,
  Reply as ReplyIcon,
  Inbox,
  Send as SendIcon,
  AllInbox
} from '@mui/icons-material'

// Component Imports
import LoadingOverlay from '../atomic/atoms/LoadingOverlay'
import VirtualList from '../atomic/organisms/VirtualList'

import { extractHtmlContent, getInitials } from '../../utils/helpers'
import McPageHelpers from '../../../../pages/mc-components/McPageHelpers'
import { last } from 'lodash'

const getDeepestMessage = (message) => {
  if (message.replies && message.replies.length > 0) {
    return getDeepestMessage(last(message.replies))
  }
  return message
}

const getRelativeTime = (millis, t) => {
  if (!millis || millis <= 0) return t('mailTemplate.mailbox.noDate')
  const now = Date.now()
  const diff = now - millis
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)

  if (minutes < 1) return t('mailTemplate.mailbox.time.now', 'Just now')
  if (minutes < 60) return t('mailTemplate.mailbox.time.minutesAgo', '{{count}}m ago', { count: minutes })
  if (hours < 24) return t('mailTemplate.mailbox.time.hoursAgo', '{{count}}h ago', { count: hours })
  if (days < 7) return t('mailTemplate.mailbox.time.daysAgo', '{{count}}d ago', { count: days })

  const userLocale = McPageHelpers.localeMap[localStorage.getItem('lang')] || 'en-US'

  return new Intl.DateTimeFormat(userLocale, {
    month: 'short',
    day: 'numeric'
  }).format(new Date(millis))
}

const countTotalMessages = (emailArr) => {
  if (!emailArr) return 0

  return emailArr.length
}

const hasAttachments = (emailArr) => {
  if (!emailArr) return false

  return emailArr.some(msg => msg?.attachments?.length > 0)
}

const FolderTab = ({ icon: Icon, label, count, isActive, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.75,
      px: 1.75,
      py: 0.875,
      cursor: 'pointer',
      borderRadius: '10px',
      bgcolor: isActive
        ? 'primary.main'
        : 'transparent',
      color: isActive
        ? '#fff'
        : 'text.secondary',
      transition: 'all 0.15s ease',
      '&:hover': {
        bgcolor: isActive
          ? 'primary.main'
          : 'action.hover'
      }
    }}
  >
    <Icon sx={{
      fontSize: 18,
      color: isActive
        ? '#fff'
        : 'text.secondary'
    }} />
    <Typography sx={{
      fontSize: '0.84rem',
      fontWeight: 600,
      whiteSpace: 'nowrap',
      lineHeight: 1,
      color: isActive
        ? '#fff'
        : 'text.secondary'
    }}>
      {label}
    </Typography>
    <Box sx={{
      bgcolor: isActive
        ? 'rgba(255,255,255,0.25)'
        : '#e0e0e0',
      color: isActive
        ? '#fff'
        : 'text.disabled',
      borderRadius: '10px',
      px: 0.625,
      minWidth: 18,
      height: 16,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '0.6rem',
      fontWeight: 700,
      lineHeight: 1
    }}>
      {count}
    </Box>
  </Box>
)

const EmailListItem = ({ emailArr, isActive, onClick, t }) => {
  const mainEmail = emailArr?.[0]
  const latestMessage = getDeepestMessage(mainEmail)
  const isIncoming = latestMessage?.type === 'INCOMING'

  const displayName = isIncoming
    ? (latestMessage.from_name || t('mailTemplate.mailbox.unknownSender'))
    : (latestMessage?.to?.[0]?.name || t('mailTemplate.mailbox.unknownRecipient'))

  const subject = mainEmail?.subject || t('mailTemplate.mailbox.noSubject')

  const bodyPreview = extractHtmlContent(mainEmail?.bodyHtml) || ''

  const messageCount = countTotalMessages(emailArr)
  const showAttachment = hasAttachments(emailArr)
  const timestamp = mainEmail?.sentTimeMillis

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        gap: 1.5,
        px: 2,
        py: 1.5,
        cursor: 'pointer',
        position: 'relative',
        borderBottom: '1px solid',
        borderBottomColor: 'divider',
        bgcolor: '#fff',
        transition: 'background-color 0.15s ease',
        '&:hover': {
          bgcolor: '#f5f5f5'
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: '3px',
          borderRadius: '0 2px 2px 0',
          bgcolor: isActive
            ? 'primary.main'
            : 'transparent'
        }
      }}
    >
      {/* Avatar */}
      <Box sx={{
        width: 40,
        height: 40,
        borderRadius: '50%',
        bgcolor: 'primary.50',
        color: 'primary.main',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: '0.8rem',
        flexShrink: 0,
        mt: 0.25
      }}>
        {getInitials(displayName)}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
        {/* Top row: name + time */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.25 }}>
          <Typography sx={{
            fontWeight: 600,
            fontSize: '0.85rem',
            lineHeight: 1.3,
            color: 'text.primary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {displayName}
          </Typography>
          <Typography sx={{
            fontSize: '0.7rem',
            color: 'text.disabled',
            whiteSpace: 'nowrap',
            flexShrink: 0
          }}>
            {getRelativeTime(timestamp, t)}
          </Typography>
        </Box>

        {/* Subject */}
        <Typography sx={{
          fontSize: '0.8rem',
          fontWeight: 500,
          color: 'text.secondary',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          lineHeight: 1.4,
          mb: 0.25
        }}>
          {subject}
        </Typography>

        {/* Body preview */}
        <Typography sx={{
          fontSize: '0.75rem',
          color: 'text.disabled',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          lineHeight: 1.4,
          mb: 0.5
        }}>
          {bodyPreview}
        </Typography>

        {/* Bottom row: badges */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {messageCount > 1 && (
            <Chip
              size="small"
              icon={<ReplyIcon sx={{ fontSize: '0.75rem !important' }} />}
              label={messageCount}
              sx={{
                height: 20,
                fontSize: '0.65rem',
                fontWeight: 600,
                bgcolor: 'action.hover',
                color: 'text.secondary',
                '& .MuiChip-label': { px: 0.5 },
                '& .MuiChip-icon': { ml: 0.5, mr: -0.25, color: 'text.disabled' }
              }}
            />
          )}
          {showAttachment && (
            <AttachFile sx={{ fontSize: '0.85rem', color: 'text.disabled', transform: 'rotate(45deg)' }} />
          )}
        </Box>
      </Box>
    </Box>
  )
}

const EmptyState = ({ setOpenCompose, t, folderLabel }) => (
  <Box sx={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    p: 4
  }}>
    <Box sx={{
      maxWidth: 360,
      textAlign: 'center',
      p: 5
    }}>
      <Box sx={{
        width: 72,
        height: 72,
        borderRadius: '50%',
        bgcolor: 'action.hover',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mx: 'auto',
        mb: 2.5
      }}>
        <MailOutline sx={{ fontSize: 36, color: 'text.disabled' }} />
      </Box>

      <Typography sx={{ fontWeight: 600, fontSize: '1.05rem', mb: 1, color: 'text.primary' }}>
        {folderLabel
          ? t('mailTemplate.mailbox.list.noDataInFolder', 'No emails in {{folder}}', { folder: folderLabel })
          : t('mailTemplate.mailbox.list.noData')
        }
      </Typography>

      <Typography sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.6, fontSize: '0.85rem' }}>
        {t('mailTemplate.mailbox.list.noDataDescription')}
      </Typography>

      {setOpenCompose && (
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setOpenCompose(true)}
          sx={{
            borderRadius: 2,
            height: 38,
            px: 3,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.85rem'
          }}
        >
          {t('mailTemplate.mailbox.list.noDataAction')}
        </Button>
      )}
    </Box>
  </Box>
)

const MailContentList = props => {
  const {
    fetching,
    filteredEmailArrays,
    searchTerm,
    setSearchTerm,
    emailArrays,
    activeEmailArr,
    setActiveEmailArr,
    setOpenCompose,
    reloadMessages,
    activeFolder,
    setActiveFolder,
    folderCounts
  } = props

  const { t } = useTranslation()
  const isEmpty = !emailArrays.length || !filteredEmailArrays.length

  const folders = [
    { key: 'all', icon: AllInbox, label: t('mailTemplate.mailbox.folders.all', 'All'), count: folderCounts.all },
    { key: 'inbox', icon: Inbox, label: t('mailTemplate.mailbox.folders.inbox', 'Inbox'), count: folderCounts.inbox },
    { key: 'sent', icon: SendIcon, label: t('mailTemplate.mailbox.folders.sent', 'Sent'), count: folderCounts.sent }
  ]

  const activeFolderLabel = folders.find(f => f.key === activeFolder)?.label || ''

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', bgcolor: '#fff' }}>
      {/* Toolbar */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        py: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: '#fff',
        flexShrink: 0
      }}>
        <Box sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          bgcolor: 'action.hover',
          borderRadius: 2,
          px: 1.5,
          py: 0.5,
          minWidth: 0
        }}>
          <Search sx={{ fontSize: 20, color: 'text.disabled', flexShrink: 0 }} />
          <InputBase
            value={searchTerm ?? ''}
            onChange={e => setSearchTerm(String(e.target.value))}
            placeholder={t('mailTemplate.mailbox.search')}
            sx={{ fontSize: '0.85rem', flex: 1, minWidth: 0 }}
            fullWidth
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          <Tooltip title={t('mailTemplate.mailbox.send.composeTitle')} arrow>
            <IconButton
              size="small"
              onClick={() => setOpenCompose(true)}
              sx={{
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                width: 32,
                height: 32,
                '&:hover': { bgcolor: 'primary.dark' }
              }}
            >
              <Add sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('mailTemplate.mailbox.refresh', 'Refresh')} arrow>
            <span>
              <IconButton
                size="small"
                onClick={reloadMessages}
                disabled={fetching}
                sx={{ width: 32, height: 32 }}
              >
                <Cached sx={{ fontSize: 20 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Folder Tabs */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: '#fff',
        flexShrink: 0,
        px: 1.5,
        py: 1
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          bgcolor: '#f5f5f5',
          borderRadius: '12px',
          p: 0.5
        }}>
          {folders.map(folder => (
            <FolderTab
            key={folder.key}
            icon={folder.icon}
            label={folder.label}
            count={folder.count}
            isActive={activeFolder === folder.key}
            onClick={() => setActiveFolder(folder.key)}
          />
          ))}
        </Box>
      </Box>

      {/* Email list */}
      <Box sx={{ flex: 1, position: 'relative', bgcolor: '#fff', overflow: 'hidden' }}>
        {fetching
          ? <LoadingOverlay />
          : isEmpty
            ? (
                <EmptyState
                  setOpenCompose={!emailArrays.length
                    ? setOpenCompose
                    : null}
                  t={t}
                  folderLabel={activeFolder !== 'all'
                    ? activeFolderLabel
                    : null}
                />
              )
            : (
                <VirtualList
                  items={filteredEmailArrays}
                  estimateSize={150}
                  overscan={5}
                  getItemKey={(emailArr, index) =>
                    emailArr?.[0]?.messageId || `email-${index}-${emailArr?.[0]?.sentTimeMillis}`}
                  renderItem={({ item: emailArr }) => (
                    <EmailListItem
                      emailArr={emailArr}
                      isActive={activeEmailArr === emailArr}
                      onClick={() => setActiveEmailArr(emailArr)}
                      t={t}
                    />
                  )}
                />
              )
        }
      </Box>
    </Box>
  )
}

export default MailContentList
